/**
 * Rampa de ACENTO derivada del color del consultorio.
 *
 * `Organizacion.marca.colorPrimario` es un hex que eligio una persona, no un
 * disenador: puede ser amarillo limon, rosa palido o casi negro. Usarlo tal
 * cual como fondo de boton rompe el contraste en la mitad de los casos. Aqui
 * se conserva lo que lo identifica —el TONO y, acotado, el CROMA— y se fija la
 * LUMINOSIDAD en OKLCH a los pasos del sistema visual (97.8 · 95.4 · 90.2 · 81
 * · 71 · 54.8 · 40 · 33 · 28 %). Despues se comprueba el contraste y, si no
 * alcanza, se mueve la luminosidad hasta que alcanza. Nunca se entrega una
 * rampa que no cumpla AA.
 *
 * Garantias (probadas en `acento.test.ts` con colores extremos):
 *
 * - Claro: texto `--accent-on` sobre `--accent-deep` >= 4.5:1, y texto
 *   `--accent-text` (= deep) sobre `--accent-wash` >= 4.5:1.
 * - Oscuro: texto `--accent-on` sobre `--accent-deep` >= 4.5:1, y
 *   `--accent-text` sobre `--accent-wash` (compuesto sobre la tarjeta) >= 4.5:1.
 * - `--accent-emphasis` (indicador activo, anillo de foco) >= 3:1 contra el
 *   lienzo y la tarjeta en los dos modos (WCAG 1.4.11).
 * - `--accent-strong` (el unico bloque profundo por pantalla) admite texto
 *   blanco y blanco al 90 % a >= 4.5:1.
 *
 * Funcion pura: mismo hex, misma rampa. No toca el DOM.
 */
import {
  ajustarAGama,
  componer,
  contraste,
  formatoMui,
  formatoOklch,
  hexARgb,
  oklchARgb,
  rgbAOklch,
  type Oklch,
  type Rgb,
} from './color';
import { colorDeRol, type ModoResuelto } from './hojas';

/** Nombres de las variables que produce la rampa, iguales en los dos modos. */
export const VARIABLES_ACENTO = [
  '--accent-deep',
  '--accent-night',
  '--accent-active',
  '--accent-emphasis',
  '--accent-mist',
  '--accent-wash',
  '--accent-text',
  '--accent-on',
  '--accent-on-muted',
  '--accent-strong',
  '--accent-strong-2',
] as const;

export type VariableAcento = (typeof VARIABLES_ACENTO)[number];
export type RampaCss = Record<VariableAcento, string>;

/** Lo minimo que MUI necesita del acento para calcular sus propios estados. */
export interface AcentoMui {
  main: string;
  dark: string;
  light: string;
  contrastText: string;
}

export interface Acento {
  claro: RampaCss;
  oscuro: RampaCss;
  mui: Record<ModoResuelto, AcentoMui>;
  /** Contrastes medidos, para pruebas y diagnostico. */
  contrastes: Record<
    ModoResuelto,
    { onSobreDeep: number; textoSobreWash: number; enfasis: number }
  >;
}

/** Superficies contra las que se mide. Salen de las hojas, no de constantes. */
export interface Superficies {
  claro: { raised: Rgb; canvas: Rgb };
  oscuro: { raised: Rgb; canvas: Rgb };
}

export function superficiesDeLasHojas(): Superficies {
  return {
    claro: {
      raised: colorDeRol('--bg-raised', 'claro'),
      canvas: colorDeRol('--bg-canvas', 'claro'),
    },
    oscuro: {
      raised: colorDeRol('--bg-raised', 'oscuro'),
      canvas: colorDeRol('--bg-canvas', 'oscuro'),
    },
  };
}

/**
 * Perfil de croma de la rampa de referencia, relativo a su pico (0.109 en el
 * paso 5). Los extremos claros y oscuros llevan menos croma: un rocio muy
 * saturado deja de leerse como fondo, y un profundo muy saturado vibra.
 */
const PERFIL = {
  p1: 0.015 / 0.109,
  p2: 0.035 / 0.109,
  p3: 0.07 / 0.109,
  p4: 0.099 / 0.109,
  p5: 1,
  p6: 0.076 / 0.109,
  p7: 0.06 / 0.109,
  p8: 0.05 / 0.109,
  fuerteOscuro: 0.065 / 0.109,
  fuerteOscuro2: 0.055 / 0.109,
  hoverOscuro: 0.1 / 0.109,
};

/** Croma maximo del pico: un instrumento clinico, no un cartel. */
const CROMA_MAX = 0.15;
/** Por debajo de esto el color de marca se trata como gris (tono sin sentido). */
const UMBRAL_ACROMATICO = 0.02;
/** Una marca cromatica pero muy apagada (rosa palido) se sube hasta aqui para
 * que la seleccion se distinga del gris de la interfaz. */
const CROMA_MIN = 0.06;

const BLANCO: Rgb = { r: 1, g: 1, b: 1, alfa: 1 };
const AA = 4.5;
const NO_TEXTO = 3;
const PASO_L = 0.005;

/**
 * Pasos de la rampa por luminosidad. El color del consultorio pertenece al paso
 * mas cercano, y el croma del pico se calcula para que ESE paso conserve el
 * croma que eligio el consultorio. Antes el color se tomaba siempre como el pico
 * (paso 5): un verde profundo de croma 0.073 salia en el boton con 0.051, gris.
 */
const PASOS: ReadonlyArray<readonly [number, number]> = [
  [0.954, PERFIL.p1],
  [0.902, PERFIL.p2],
  [0.81, PERFIL.p3],
  [0.71, PERFIL.p4],
  [0.548, PERFIL.p5],
  [0.4, PERFIL.p6],
  [0.33, PERFIL.p7],
  [0.28, PERFIL.p8],
];

function perfilDelPaso(l: number): number {
  let mejor = PASOS[0]!;
  for (const paso of PASOS) if (Math.abs(paso[0] - l) < Math.abs(mejor[0] - l)) mejor = paso;
  return mejor[1];
}

/**
 * Ventana en la que el color del consultorio ya ES un profundo: se usa tal cual
 * en el boton principal si cumple los contrastes. Fuera de ella (un amarillo, un
 * rosa palido) se deriva el profundo como siempre.
 */
const PROFUNDO_MIN = 0.3;
const PROFUNDO_MAX = 0.45;

function colorBase(hex: string): Oklch {
  const rgb = hexARgb(hex);
  // Un hex invalido no puede tumbar la interfaz: se cae a un acento neutro.
  return rgb ? rgbAOklch(rgb) : { l: 0.4, c: 0, h: 0 };
}

export function derivarAcento(
  colorPrimario: string,
  superficies: Superficies = superficiesDeLasHojas(),
): Acento {
  const base = colorBase(colorPrimario);
  const acromatico = base.c < UMBRAL_ACROMATICO;
  const pico = acromatico
    ? base.c
    : Math.min(CROMA_MAX, Math.max(CROMA_MIN, base.c / perfilDelPaso(base.l)));
  const h = base.h;

  const paso = (l: number, perfil: number, alfa = 1): Oklch =>
    ajustarAGama({ l: Math.min(1, Math.max(0, l)), c: pico * perfil, h, alfa });
  const rgb = (c: Oklch) => oklchARgb({ ...c, alfa: 1 });
  const sobre = (c: Oklch, fondo: Rgb) => componer(oklchARgb(c), fondo);

  // ------------------------------ Claro ------------------------------
  const washClaro = paso(0.954, PERFIL.p1);
  const cumpleComoProfundo = (c: Oklch) =>
    contraste(BLANCO, rgb(c)) >= AA &&
    contraste(rgb(c), sobre(washClaro, superficies.claro.raised)) >= AA;
  const propio = ajustarAGama({ ...base, alfa: 1 });
  const usarPropio =
    !acromatico && base.l >= PROFUNDO_MIN && base.l <= PROFUNDO_MAX && cumpleComoProfundo(propio);
  let deepClaro = usarPropio ? propio : paso(0.4, PERFIL.p6);
  while (
    !usarPropio &&
    deepClaro.l > 0.05 &&
    (contraste(BLANCO, rgb(deepClaro)) < AA ||
      contraste(rgb(deepClaro), sobre(washClaro, superficies.claro.raised)) < AA)
  ) {
    deepClaro = paso(deepClaro.l - PASO_L, PERFIL.p6);
  }
  const nightClaro = paso(Math.min(0.33, deepClaro.l - 0.07), PERFIL.p7);
  const activeClaro = paso(Math.min(0.28, deepClaro.l - 0.12), PERFIL.p8);

  let enfasisClaro = paso(0.548, PERFIL.p5);
  while (
    enfasisClaro.l > 0.05 &&
    Math.min(
      contraste(rgb(enfasisClaro), superficies.claro.raised),
      contraste(rgb(enfasisClaro), superficies.claro.canvas),
    ) < NO_TEXTO
  ) {
    enfasisClaro = paso(enfasisClaro.l - PASO_L, PERFIL.p5);
  }
  const mistClaro = paso(0.902, PERFIL.p2);

  // ------------------------------ Oscuro ------------------------------
  let deepOscuro = paso(0.548, PERFIL.p5);
  while (deepOscuro.l > 0.05 && contraste(BLANCO, rgb(deepOscuro)) < AA) {
    deepOscuro = paso(deepOscuro.l - PASO_L, PERFIL.p5);
  }
  const nightOscuro = paso(Math.min(0.48, deepOscuro.l - 0.05), PERFIL.hoverOscuro);
  const activeOscuro = paso(deepOscuro.l - 0.1, PERFIL.p5);
  const washOscuro: Oklch = { ...deepOscuro, alfa: 0.18 };

  let textoOscuro = paso(0.81, PERFIL.p3);
  const textoOscuroCumple = (c: Oklch) =>
    Math.min(
      contraste(rgb(c), superficies.oscuro.raised),
      contraste(rgb(c), superficies.oscuro.canvas),
      contraste(rgb(c), sobre(washOscuro, superficies.oscuro.raised)),
      contraste(rgb(c), sobre(washOscuro, superficies.oscuro.canvas)),
    ) >= AA;
  while (textoOscuro.l < 0.99 && !textoOscuroCumple(textoOscuro)) {
    textoOscuro = paso(textoOscuro.l + PASO_L, PERFIL.p3);
  }

  let enfasisOscuro = paso(0.71, PERFIL.p4);
  while (
    enfasisOscuro.l < 0.99 &&
    Math.min(
      contraste(rgb(enfasisOscuro), superficies.oscuro.raised),
      contraste(rgb(enfasisOscuro), superficies.oscuro.canvas),
    ) < NO_TEXTO
  ) {
    enfasisOscuro = paso(enfasisOscuro.l + PASO_L, PERFIL.p4);
  }
  const mistOscuro: Oklch = { ...enfasisOscuro, alfa: 0.32 };

  // Bloque profundo unico en oscuro: blanco y blanco al 90 % a AA.
  const blanco90: Oklch = { l: 1, c: 0, h: 0, alfa: 0.9 };
  let fuerteOscuro = paso(0.34, PERFIL.fuerteOscuro);
  while (
    fuerteOscuro.l > 0.05 &&
    contraste(sobre(blanco90, rgb(fuerteOscuro)), rgb(fuerteOscuro)) < AA
  ) {
    fuerteOscuro = paso(fuerteOscuro.l - PASO_L, PERFIL.fuerteOscuro);
  }
  const fuerteOscuro2 = paso(Math.min(0.29, fuerteOscuro.l - 0.05), PERFIL.fuerteOscuro2);

  const on = formatoOklch({ l: 1, c: 0, h: 0 });
  const onMuted = formatoOklch(blanco90);

  const claro: RampaCss = {
    '--accent-deep': formatoOklch(deepClaro),
    '--accent-night': formatoOklch(nightClaro),
    '--accent-active': formatoOklch(activeClaro),
    '--accent-emphasis': formatoOklch(enfasisClaro),
    '--accent-mist': formatoOklch(mistClaro),
    '--accent-wash': formatoOklch(washClaro),
    '--accent-text': formatoOklch(deepClaro),
    '--accent-on': on,
    '--accent-on-muted': onMuted,
    '--accent-strong': formatoOklch(deepClaro),
    '--accent-strong-2': formatoOklch(nightClaro),
  };

  const oscuro: RampaCss = {
    '--accent-deep': formatoOklch(deepOscuro),
    '--accent-night': formatoOklch(nightOscuro),
    '--accent-active': formatoOklch(activeOscuro),
    '--accent-emphasis': formatoOklch(enfasisOscuro),
    '--accent-mist': formatoOklch(mistOscuro),
    '--accent-wash': formatoOklch(washOscuro),
    '--accent-text': formatoOklch(textoOscuro),
    '--accent-on': on,
    '--accent-on-muted': onMuted,
    '--accent-strong': formatoOklch(fuerteOscuro),
    '--accent-strong-2': formatoOklch(fuerteOscuro2),
  };

  const hex = (c: Oklch) => formatoMui(rgb(c));

  return {
    claro,
    oscuro,
    mui: {
      claro: {
        main: hex(deepClaro),
        dark: hex(nightClaro),
        light: hex(enfasisClaro),
        contrastText: formatoMui(BLANCO),
      },
      oscuro: {
        main: hex(deepOscuro),
        dark: hex(nightOscuro),
        light: hex(enfasisOscuro),
        contrastText: formatoMui(BLANCO),
      },
    },
    contrastes: {
      claro: {
        onSobreDeep: contraste(BLANCO, rgb(deepClaro)),
        textoSobreWash: contraste(rgb(deepClaro), sobre(washClaro, superficies.claro.raised)),
        enfasis: Math.min(
          contraste(rgb(enfasisClaro), superficies.claro.raised),
          contraste(rgb(enfasisClaro), superficies.claro.canvas),
        ),
      },
      oscuro: {
        onSobreDeep: contraste(BLANCO, rgb(deepOscuro)),
        textoSobreWash: contraste(rgb(textoOscuro), sobre(washOscuro, superficies.oscuro.raised)),
        enfasis: Math.min(
          contraste(rgb(enfasisOscuro), superficies.oscuro.raised),
          contraste(rgb(enfasisOscuro), superficies.oscuro.canvas),
        ),
      },
    },
  };
}

/** Hoja CSS con la rampa: `:root{...}` para claro y `:root[data-tema='oscuro']{...}`. */
export function hojaDeAcento(acento: Acento): string {
  const bloque = (rampa: RampaCss) =>
    VARIABLES_ACENTO.map((nombre) => `${nombre}:${rampa[nombre]};`).join('');
  return `:root{${bloque(acento.claro)}}:root[data-tema='oscuro']{${bloque(acento.oscuro)}}`;
}

/**
 * Resalte derivado del color SECUNDARIO del consultorio: fondos suaves de
 * contexto (tamizaje previo, estados vacios). Se conserva su TONO y nada mas:
 * la luminosidad y el croma se fijan en el rango de los lavados de la casa
 * Gelux (el «rocio» de Telmo: 95-97.5 %, croma <= 0.022). Un secundario vivo,
 * como un verde menta, daba un bloque saturado que gritaba mas que la accion
 * principal. En oscuro es el mismo tono en un plano apenas mas claro que la
 * tarjeta.
 */
export interface Resalte {
  claro: { fondo: string; borde: string };
  oscuro: { fondo: string; borde: string };
}

/** Croma maximo de un lavado: el del rocio de Telmo con un poco de margen. */
export const CROMA_DE_RESALTE = 0.022;

export function derivarResalte(colorSecundario: string): Resalte | null {
  const rgb = hexARgb(colorSecundario);
  if (!rgb) return null;
  const base = rgbAOklch(rgb);
  if (base.c < UMBRAL_ACROMATICO) return null;
  const c = Math.min(base.c, CROMA_DE_RESALTE);
  const lClaro = Math.min(0.975, Math.max(0.955, base.l));
  const tono = (l: number, croma: number) =>
    formatoOklch(ajustarAGama({ l, c: croma, h: base.h, alfa: 1 }));
  return {
    claro: { fondo: tono(lClaro, c), borde: tono(lClaro - 0.1, Math.min(c * 1.4, 0.08)) },
    oscuro: { fondo: tono(0.27, Math.min(c, 0.035)), borde: tono(0.38, Math.min(c, 0.05)) },
  };
}

/** Hoja con el resalte, o nada si el secundario no sirve (se queda el respaldo). */
export function hojaDeResalte(resalte: Resalte | null): string {
  if (!resalte) return '';
  const bloque = (r: { fondo: string; borde: string }) =>
    `--highlight-bg:${r.fondo};--highlight-border:${r.borde};`;
  return `:root{${bloque(resalte.claro)}}:root[data-tema='oscuro']{${bloque(resalte.oscuro)}}`;
}
